// src/api/strapi.js

import React from 'react';

// Base URL for Strapi; ensure REACT_APP_STRAPI_URL is set in .env
export const STRAPI_URL =
  process.env.REACT_APP_STRAPI_URL?.replace(/\/+$/, '') ||
  'http://localhost:1337';

/**
 * Helper: render nested rich-text children from Strapi's blocks field
 */
const renderTextChildren = (children) => {
  if (!Array.isArray(children)) return null;
  return children.map((child, idx) => {
    let text = child.text;
    if (typeof text === 'object' && text !== null) {
      text = child.text.name || '';
    }
    if (text === '\n') {
      return <br key={idx} />;
    }
    let el = <React.Fragment key={idx}>{text}</React.Fragment>;
    if (child.bold) el = <strong key={`${idx}-b`}>{el}</strong>;
    if (child.italic) el = <em key={`${idx}-i`}>{el}</em>;
    if (child.underline) el = <u key={`${idx}-u`}>{el}</u>;
    if (child.strikethrough) el = <s key={`${idx}-s`}>{el}</s>;
    if (child.type === 'link') {
      return (
        <a key={`${idx}-link`} href={child.url} target="_blank" rel="noopener noreferrer">
          {renderTextChildren(child.children)}
        </a>
      );
    }
    return el;
  });
};

/**
 * Render Strapi Rich Text (Blocks) array into React components
 */
export const renderStrapiRichText = (blocks) => {
  if (!Array.isArray(blocks)) return null;
  return blocks.map((block, idx) => {
    const children = Array.isArray(block.children) ? block.children : [];
    switch (block.type) {
      case 'paragraph':
        return <p key={idx}>{renderTextChildren(children)}</p>;
      case 'heading': {
        const Tag = `h${block.level || 1}`;
        return <Tag key={idx}>{renderTextChildren(children)}</Tag>;
      }
      case 'list': {
        const Tag = block.format === 'ordered' ? 'ol' : 'ul';
        return (
          <Tag key={idx}>
            {children.map((li, j) => (
              <li key={j}>{renderTextChildren(li.children || [])}</li>
            ))}
          </Tag>
        );
      }
      case 'quote':
        return <blockquote key={idx}>{renderTextChildren(children)}</blockquote>;
      case 'image': {
        const img = block.image || {};
        if (!img.url) return null;
        const src = img.url.startsWith('http')
          ? img.url
          : `${STRAPI_URL}${img.url}`;
        const caption = typeof img.caption === 'string' ? img.caption : '';
        return (
          <figure key={idx} className="strapi-richtext-image">
            <img src={src} alt={img.alternativeText || ''} />
            {caption && <figcaption>{caption}</figcaption>}
          </figure>
        );
      }
      case 'code':
        return (
          <pre key={idx}>
            <code>{children.map(c => c.text).join('')}</code>
          </pre>
        );
      default:
        console.warn('Unsupported rich-text block type:', block);
        return null;
    }
  });
};

/**
 * Fetch all authors
 */
export async function fetchAuthors() {
  const url = `${STRAPI_URL}/api/authors?populate=Profile`;
  try {
    const res = await fetch(url);
    if (!res.ok) {
      console.error(`fetchAuthors HTTP error: ${res.status} ${res.statusText}`);
      throw new Error(`fetchAuthors error: ${res.status}`);
    }
    const json = await res.json();
    const items = Array.isArray(json.data) ? json.data : [];
    
    return items.map(item => {
      const attrs = item.attributes ?? item;
      const id = item.id;
      
      const profileFieldData = attrs.Profile?.data?.attributes ?? attrs.Profile?.attributes ?? attrs.Profile;
      const profilePath = profileFieldData?.url || profileFieldData?.formats?.large?.url || profileFieldData?.formats?.medium?.url || null;
      const image = profilePath ? (profilePath.startsWith('http') ? profilePath : `${STRAPI_URL}${profilePath}`) : null;

      const bioText = attrs.Bio ?? '';
      
      return {
        id,
        name: attrs.Name ?? '', // Assuming API ID is 'Name' (Capitalized)
        title: attrs.Title ?? '',
        linkedin: attrs.Linkedin ?? '',
        bio: bioText ? bioText.split('\n\n') : [],
        quote: attrs.Quote ?? '',
        image,
      };
    }).filter(author => author !== null);
  } catch (error) {
    console.error("Error inside fetchAuthors:", error);
    throw error;
  }
}

/**
 * Fetch all blog posts
 */
export async function fetchPosts() {
  const url = `${STRAPI_URL}/api/posts?populate=*&sort[Published]=DESC`;
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`fetchPosts error: ${res.status}`);
    const json = await res.json();
    const items = Array.isArray(json.data) ? json.data : [];

    return items.map(item => {
      if (!item) return null;
      const attrs = item.attributes ?? item;
      const id = item.id;
      
      const imgField = attrs.Image;
      const imgPath = imgField?.data?.attributes?.url || imgField?.url || null;
      const imageUrl = imgPath ? (imgPath.startsWith('http') ? imgPath : `${STRAPI_URL}${imgPath}`) : null;

      const authorField = attrs.author?.data?.attributes ?? attrs.author;
      const authorProfileField = authorField?.Profile?.data?.attributes ?? authorField?.Profile;
      const authorProfilePath = authorProfileField?.url || null;
      const authorImageUrl = authorProfilePath ? (authorProfilePath.startsWith('http') ? authorProfilePath : `${STRAPI_URL}${authorProfilePath}`) : null;
      
      const contentBlocks = Array.isArray(attrs.MainContent) ? attrs.MainContent : [];
      const summary = contentBlocks
        .flatMap(b => (b.children || []).map(c => c.text || ''))
        .join(' ')
        .slice(0, 150)
        .trim() + (contentBlocks.length ? '…' : '');

      const rawDate = attrs.Published ?? attrs.publishedAt ?? null;

      return { 
        id, 
        title: attrs.Headline ?? attrs.title ?? '', 
        slug: attrs.slug ?? '', 
        rawDate,
        date: rawDate ? new Date(rawDate).toLocaleDateString() : '', 
        summary, 
        imageUrl, 
        author: { name: authorField?.Name ?? '', imageUrl: authorImageUrl }, 
        contentBlocks 
      };
    }).filter(post => post !== null);
  } catch (error) {
    console.error("Error inside fetchPosts:", error);
    throw error;
  }
}

/**
 * Fetch a single blog post by its slug
 */
export async function fetchPost(slug) {
  if (!slug) {
    console.error("fetchPost called with no slug.");
    return null;
  }
  const url = `${STRAPI_URL}/api/posts?filters[slug][$eq]=${encodeURIComponent(slug)}&populate=*`;
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`fetchPost error: ${res.status}`);
    const json = await res.json();
    const data = Array.isArray(json.data) ? json.data : [];

    if (data.length === 0) {
      console.warn(`No post found for slug: ${slug}`);
      return null;
    }

    const item = data[0];
    const attrs = item.attributes ?? item;
    
    // Process single post data similarly to fetchPosts for consistency
    const imgField = attrs.Image;
    const imgPath = imgField?.data?.attributes?.url || imgField?.url || null;
    const imageUrl = imgPath ? (imgPath.startsWith('http') ? imgPath : `${STRAPI_URL}${imgPath}`) : null;

    const authorField = attrs.author?.data?.attributes ?? attrs.author;
    const authorProfileField = authorField?.Profile?.data?.attributes ?? authorField?.Profile;
    const authorProfilePath = authorProfileField?.url || null;
    const authorImageUrl = authorProfilePath ? (authorProfilePath.startsWith('http') ? authorProfilePath : `${STRAPI_URL}${authorProfilePath}`) : null;

    const rawDate = attrs.Published ?? attrs.publishedAt ?? null;

    return {
      id: item.id,
      title: attrs.Headline ?? attrs.title ?? '',
      slug: attrs.slug ?? '',
      rawDate,
      date: rawDate ? new Date(rawDate).toLocaleDateString() : '',
      imageUrl,
      author: { name: authorField?.Name ?? '', imageUrl: authorImageUrl },
      contentBlocks: Array.isArray(attrs.MainContent) ? attrs.MainContent : []
    };
  } catch (error) {
    console.error(`Error inside fetchPost for slug "${slug}":`, error);
    throw error;
  }
}


/**
 * Fetch all reports
 */
export async function fetchReports() {
  const url = `${STRAPI_URL}/api/reports?populate=*&sort[0]=Published:desc&sort[1]=publishedAt:desc&sort[2]=createdAt:desc`;
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`fetchReports error: ${res.statusText} (status ${res.status})`);
    const json = await res.json();
    const items = Array.isArray(json.data) ? json.data : [];

    if (items.length === 0) {
      return [];
    }

    return items.map(item => {
      if (!item) return null;
      const attrs = item.attributes ?? item;
      if (!attrs || typeof attrs !== 'object') return null;

      const coverFieldData = attrs.Cover?.data?.attributes ?? attrs.Cover?.attributes ?? attrs.Cover;
      const coverPath = coverFieldData?.url || coverFieldData?.formats?.large?.url || null;
      const coverUrl = coverPath ? (coverPath.startsWith('http') ? coverPath : `${STRAPI_URL}${coverPath}`) : null;
      
      const rawDate = attrs.Published ?? attrs.publishedAt ?? attrs.createdAt ?? null;

      return {
        id: item.id,
        headline: attrs.Headline ?? '',
        summary: attrs.Summary ?? '',
        slug: attrs.slug ?? '',
        coverUrl,
        rawDate,
        isComingSoon: attrs.isComingSoon === true,
      };
    }).filter(report => report !== null)
      .sort((a, b) => {
        const dateA = a.rawDate ? new Date(a.rawDate).getTime() : 0;
        const dateB = b.rawDate ? new Date(b.rawDate).getTime() : 0;
        return dateB - dateA;
      });
  } catch (error) {
    console.error("Error inside fetchReports:", error);
    throw error;
  }
}

/**
 * Fetch a single report by its slug
 */
export async function fetchReport(slug) {
  if (!slug) {
    console.error("fetchReport called with no slug.");
    return null;
  }
  const url = `${STRAPI_URL}/api/reports?filters[slug][$eq]=${encodeURIComponent(slug)}&populate=*`;
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`API error fetching report: ${res.statusText}`);
    const json = await res.json();
    const data = Array.isArray(json.data) ? json.data : [];

    if (data.length === 0) {
      console.warn(`No report found for slug "${slug}".`);
      return null;
    }

    const item = data[0];
    const attrs = item.attributes ?? item;

    if (!attrs || typeof attrs !== 'object') {
      throw new Error("Processed item attributes are unexpectedly invalid.");
    }
    
    const coverFieldData = attrs.Cover?.data?.attributes ?? attrs.Cover?.attributes ?? attrs.Cover;
    const coverPath = coverFieldData?.url || coverFieldData?.formats?.large?.url || null;
    const coverUrl = coverPath ? (coverPath.startsWith('http') ? coverPath : `${STRAPI_URL}${coverPath}`) : null;

    const pdfFieldData = attrs.PDFUpload?.data?.attributes ?? attrs.PDFUpload?.attributes ?? attrs.PDFUpload;
    const pdfUrl = pdfFieldData?.url ? (pdfFieldData.url.startsWith('http') ? pdfFieldData.url : `${STRAPI_URL}${pdfFieldData.url}`) : null;
    const pdfFileName = pdfFieldData?.name ?? `${attrs.slug || 'report'}-download.pdf`;
    
    const audioFieldData = attrs.AudioRecording?.data?.attributes ?? attrs.AudioRecording?.attributes ?? attrs.AudioRecording;
    const audioRecordingUrl = audioFieldData?.url ? (audioFieldData.url.startsWith('http') ? audioFieldData.url : `${STRAPI_URL}${audioFieldData.url}`) : null;

    const pubDate = attrs.Published ?? attrs.publishedAt ?? null;

    return {
      id: item.id,
      headline: attrs.Headline ?? '',
      summary: attrs.Summary ?? '',
      slug: attrs.slug ?? '',
      published: pubDate ? new Date(pubDate).toLocaleDateString() : '',
      coverUrl,
      pdfUrl,
      pdfFileName,
      main: Array.isArray(attrs.Main) ? attrs.Main : [],
      isComingSoon: attrs.isComingSoon === true,
      useCustomCode: attrs.useCustomCode === true,
      audioRecordingUrl,
    };
  } catch (error) {
    console.error(`Error inside fetchReport for slug "${slug}":`, error);
    throw error;
  }
}
